/**
 * Materiais de item (techspec-materiais). Espelha `Items/Materiais.cs` do servidor: a mesma
 * tabela sai pelo mesmo deploy, como os hex dos metais da GDD 07.2.1.
 *
 * O id diz o material:
 * - `katana@valorite`, `bow@oak`: a variante;
 * - `katana`, `bow`: a base, que é o padrão da família (Iron, madeira comum);
 * - `oak-log`, `valorite-ore`, `valorite-ingot`: a matéria-prima;
 * - `plate:valorite`: a camada no fio da roupa.
 */

export type FamiliaMaterial = 'metal' | 'madeira' | 'couro' | 'osso';

export interface Material {
  readonly id: string;
  readonly familia: FamiliaMaterial;
  /** Nome próprio, igual nos três idiomas; a chave `material.<id>` manda na tela. */
  readonly nome: string;
  readonly padrao: boolean;
  readonly cor: string;
  /** Lingote, tora, couro ou osso (no couro e no osso é o próprio item). */
  readonly insumo: string;
  /** Minério; só metal. */
  readonly bruto?: string;
}

const M = (
  id: string, familia: FamiliaMaterial, nome: string, padrao: boolean, cor: string, insumo: string, bruto?: string
): Material => ({ id, familia, nome, padrao, cor, insumo, ...(bruto ? { bruto } : {}) });

export const MATERIAIS: readonly Material[] = [
  M('iron', 'metal', 'Iron', true, '#8a8a8a', 'iron-ingot', 'iron-ore'),
  M('dull-copper', 'metal', 'Dull Copper', false, '#7a6a55', 'dull-copper-ingot', 'dull-copper-ore'),
  M('shadow-iron', 'metal', 'Shadow Iron', false, '#3f4450', 'shadow-iron-ingot', 'shadow-iron-ore'),
  M('copper', 'metal', 'Copper', false, '#b4633a', 'copper-ingot', 'copper-ore'),
  M('bronze', 'metal', 'Bronze', false, '#8c6b3f', 'bronze-ingot', 'bronze-ore'),
  M('golden', 'metal', 'Golden', false, '#c9a227', 'golden-ingot', 'golden-ore'),
  M('agapite', 'metal', 'Agapite', false, '#a86d94', 'agapite-ingot', 'agapite-ore'),
  M('verite', 'metal', 'Verite', false, '#3f8c5a', 'verite-ingot', 'verite-ore'),
  M('valorite', 'metal', 'Valorite', false, '#5a7fb4', 'valorite-ingot', 'valorite-ore'),
  M('wood', 'madeira', 'Wood', true, '#8a6a45', 'log'),
  M('oak', 'madeira', 'Oak', false, '#b08850', 'oak-log'),
  M('ash', 'madeira', 'Ash', false, '#c8b89a', 'ash-log'),
  M('yew', 'madeira', 'Yew', false, '#8b4a2b', 'yew-log'),
  M('heartwood', 'madeira', 'Heartwood', false, '#6e8b3d', 'heartwood-log'),
  M('bloodwood', 'madeira', 'Bloodwood', false, '#8e1b1b', 'bloodwood-log'),
  M('frostwood', 'madeira', 'Frostwood', false, '#a9d3e8', 'frostwood-log'),
  // Couro e osso (proposta-armaduras-couro-e-osso §01): o id do material é o id do item; cor do hue do RunUO.
  M('leather', 'couro', 'Couro', true, '#8b5e3c', 'leather'),
  M('spined-leather', 'couro', 'Couro Espinhoso', false, '#8c6a52', 'spined-leather'),
  M('horned-leather', 'couro', 'Couro Chifrudo', false, '#b58a5a', 'horned-leather'),
  M('barbed-leather', 'couro', 'Couro Farpado', false, '#a0522d', 'barbed-leather'),
  M('bone', 'osso', 'Osso', true, '#e8e2d0', 'bone'),
  M('troll-bone', 'osso', 'Osso de Troll', false, '#9aa7b0', 'troll-bone'),
  M('daemon-bone', 'osso', 'Osso de Daemon', false, '#5a4a5e', 'daemon-bone'),
  M('dragon-bone', 'osso', 'Osso de Dragão', false, '#d9c8a3', 'dragon-bone')
];

export const MATERIAL_POR_ID: ReadonlyMap<string, Material> = new Map(MATERIAIS.map(m => [m.id, m]));

/** Os metais na ordem antiga do `hue` (0 = Iron … 8 = Valorite). Só para ler `plate:8`. */
const METAIS_LEGADO = MATERIAIS.filter(m => m.familia === 'metal').map(m => m.id);

/** As bases que ganham variante, e de qual família. A besta pesada entrou com o craft (techspec-craft). */
export const BASES_COM_MATERIAL: Readonly<Record<string, FamiliaMaterial>> = {
  heater: 'metal',
  'plate-gloves': 'metal',
  'plate-helm': 'metal',
  'plate-arms': 'metal',
  'plate-legs': 'metal',
  'plate-chest': 'metal',
  dagger: 'metal',
  katana: 'metal',
  'large-battle-axe': 'metal',
  bow: 'madeira',
  'gnarled-staff': 'madeira',
  'heavy-crossbow': 'madeira',
  // Couro cravejado e osso por material (proposta-armaduras-couro-e-osso, 27/10/2026).
  'studded-chest': 'couro',
  'studded-legs': 'couro',
  'studded-arms': 'couro',
  'studded-gloves': 'couro',
  'studded-helm': 'couro',
  'bone-helm': 'osso',
  'bone-chest': 'osso',
  'bone-legs': 'osso',
  'bone-arms': 'osso',
  'bone-gloves': 'osso'
};

/** Matéria-prima → material: `oak-log`, `valorite-ore`, `valorite-ingot`. */
const MATERIA_PRIMA: ReadonlyMap<string, Material> = new Map(
  MATERIAIS.flatMap(m => [[m.insumo, m], ...(m.bruto ? [[m.bruto, m]] : [])] as [string, Material][])
);

/**
 * Item de material fixo que não é variante (`golden-hatchet`, `golden-pickaxe`): o ícone e o overlay saem
 * da arte da base, tingidos pelo material.
 */
const ITEM_DE_MATERIAL: ReadonlyMap<string, Material> = new Map(
  ([['golden-hatchet', 'golden'], ['golden-pickaxe', 'golden']] as const).flatMap(([id, mat]) => {
    const m = MATERIAIS.find(x => x.id === mat);
    return m ? [[id, m] as [string, Material]] : [];
  })
);

export function materiaisDa(familia: FamiliaMaterial): readonly Material[] {
  return MATERIAIS.filter(m => m.familia === familia);
}

/** `katana@valorite` → `katana`. Quem não é variante devolve o próprio id. */
export function baseDe(defId: string | null | undefined): string {
  const id = (defId ?? '').trim();
  const corte = id.indexOf('@');
  return corte < 0 ? id : id.slice(0, corte);
}

export function idDaVariante(base: string, material: Material | string): string {
  const m = typeof material === 'string' ? MATERIAL_POR_ID.get(material) : material;
  return !m || m.padrao ? base : `${base}@${m.id}`;
}

/**
 * O material do item, ou `null` quando o item não tem. Variante de família errada
 * (`katana@oak`) também devolve `null`: não é item do jogo.
 */
export function abrirMaterial(defId: string | null | undefined): { base: string; material: Material | null } {
  const id = (defId ?? '').trim();
  const corte = id.indexOf('@');
  if (corte >= 0) {
    const base = id.slice(0, corte);
    const familia = BASES_COM_MATERIAL[base];
    const m = MATERIAL_POR_ID.get(id.slice(corte + 1));
    return { base, material: familia && m && !m.padrao && m.familia === familia ? m : null };
  }
  const familia = BASES_COM_MATERIAL[id];
  if (familia) return { base: id, material: materiaisDa(familia).find(m => m.padrao) ?? null };
  return { base: id, material: MATERIA_PRIMA.get(id) ?? ITEM_DE_MATERIAL.get(id) ?? null };
}

export function materialDoItem(defId: string | null | undefined): Material | null {
  return abrirMaterial(defId).material;
}

/** Minério, lingote, tora, couro ou osso: o ícone sai da arte comum, tingida. */
export function ehMateriaPrima(defId: string): boolean {
  return MATERIA_PRIMA.has(defId);
}

/** Couro ou osso cru (`spined-leather`, `dragon-bone`): a arte do couro cortado / do osso do UO, tingida. */
export function ehCouroOuOsso(defId: string): 'couro' | 'osso' | null {
  const m = MATERIA_PRIMA.get(defId);
  return m && (m.familia === 'couro' || m.familia === 'osso') ? m.familia : null;
}

export function ehMinerioOuLingote(defId: string): boolean {
  const m = MATERIA_PRIMA.get(defId);
  return !!m && m.familia === 'metal';
}

const SUFIXO_CAMADA = /:([a-z0-9-]+)$/;

/** `plate:valorite` → Valorite; `studded:spined-leather` → Couro Espinhoso. O `plate:8` antigo (índice do metal) ainda é lido. */
export function materialDaCamada(camada: string | null | undefined): Material | null {
  const m = (camada ?? '').trim().match(SUFIXO_CAMADA);
  if (!m) return null;
  const sufixo = m[1];
  const id = /^\d+$/.test(sufixo) ? METAIS_LEGADO[Number(sufixo)] : sufixo;
  const mat = id ? MATERIAL_POR_ID.get(id) : undefined;
  return mat ?? null;
}

/** Camada sem o sufixo do material: `plate:valorite` → `plate`. */
export function semMaterialNaCamada(camada: string | null | undefined): string {
  return (camada ?? '').trim().replace(SUFIXO_CAMADA, '');
}

/**
 * As matizes dos metais no cliente da UO (o `Hue` do CraftResources do ServUO): [hue, 32 tons RGB em hex], lidos do
 * `hues.mul`. A UO pinta o item com matiz cheia: cada pixel vira o tom `vermelho >> 3` (0..31) da rampa. É assim que a
 * arma e a armadura de metal saem no mundo e no ícone (Renato, 30/10: "valide se a skin do machado está correta").
 * O `cor` da tabela continua sendo a amostra da interface (chip, lista); a arte usa a rampa.
 */
const RAMPA_UO: Readonly<Record<string, readonly [number, string]>> = {
'dull-copper': [0x973, '0000080000080808101010181818182020202920293129293931313939394141394a4141524a4a5a524a625a526a625a736a62736a627b736a837b738b8373948b7b9c8b83a49483ac9c8baca494b4ac94bdac9cc5b4a4cdbda4d5c5acdecdb4'],
  'shadow-iron': [0x966, '00000800000800000808081008081010101810101818182018182020182920202929203129293131293931313939314139394139394141394a41414a4a41524a4a52524a5a52525a5a52625a5262625a6a625a6a6a62736a6273736a7b736a7b'],
  'copper': [0x96D, '0000080000080808081010101818102020182920183129203931204139294a41295241315a4a316252396a5a397362417b6a417b6a4183734a8b7b4a9483529c8b52a48b5aac945ab49c62bda462c5ac6acdac6ad5b473debd73e6c57beecd7b'],
  'bronze': [0x972, '0000080000080808081008081810101810102018102920103120183129183929184131184a39204a39205241205a4120624a296252296a5229735a297b5a317b6231836a318b6a319473399473399c7b39a48339ac8341ac8b41b48b41bd9441'],
  'golden': [0x8A5, '0000080000080808081010081818102020102929103129103931184139184a4118524a185a52206252206a5a207362207b6a298373298b7b299483299c8331a48b31ac9431b49c31bda439c5ac39cdac39d5b439debd41e6c541eecd41f6d541'],
  'agapite': [0x979, '0000080000080808101010101818182018202920203129293931314139314a39395241415a4a4162524a6a5252735a527b625a7b6a628373628b736a947b739c8373a48b7bac8b83b49483bd9c8bc5a494cdac94d5ac9cdeb4a4e6bda4eec5ac'],
  'verite': [0x89F, '000008000008080808101010181810202018292918293118313920394120414a294a4a29525229525a315a6231626a396a7339737b397b8341838b4183944a8b9c4a949c4a9ca452a4ac52acb45aacbd5ab4c55abdcd62c5d562cdde6ad5e66a'],
  'valorite': [0x8AB, '00000800000808081010101810182018202920293129293929314131394a394152394a5241525a4a5262525a6a5262735a6a7b627383627b8b6a839473839c7b8ba47b94a4839cac8ba4b48bacbd94acc59cb4cda4bdd5a4c5deaccde6b4d5ee'],
};

/** A rampa de 32 tons da UO do material, ou null (padrão, madeira, couro, osso). */
export function rampaDoMaterial(m: Material | null | undefined): Uint8ClampedArray | null {
  if (!deveTingir(m)) return null;
  const r = RAMPA_UO[m.id];
  if (!r || r[1].length !== 32 * 6) return null;
  const out = new Uint8ClampedArray(32 * 3);
  for (let i = 0; i < 32 * 3; i++) out[i] = Number.parseInt(r[1].slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** O padrão (`iron`, `wood`) é a arte original: não tinge. */
export function deveTingir(m: Material | null | undefined): m is Material {
  return !!m && !m.padrao;
}

export function corDoMaterial(m: Material | null | undefined): { r: number; g: number; b: number } | null {
  if (!deveTingir(m)) return null;
  const n = Number.parseInt(m.cor.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Shadow Iron no ícone de minério e lingote clareia 1,60×; a paleta da GDD não muda. */
export const FATOR_BRILHO_SHADOW = 1.6;

/**
 * Divisor por arquivo do minério e do lingote: média de luminância ÷ 0,506 (o brilho do
 * lingote em divisor 160). Um divisor só por família faria o degrau de quantidade virar
 * degrau de brilho.
 */
export const DIVISOR_POR_ARQUIVO: Readonly<Record<string, number>> = {
  'ore-1.png': 138,
  'ore-2.png': 111,
  'ore-3.png': 124,
  'ingot-1.png': 155,
  'ingot-2.png': 160,
  'ingot-3.png': 165
};

/**
 * Divisor de `tingirFonte`: tabela no minério e no lingote, 160 nas placas (o visual de sempre)
 * e, no resto (arma, escudo, tora), o brilho médio do próprio arquivo, para a média da arte cair
 * na cor do material, como no protótipo das madeiras.
 */
export type Divisor = number | 'media';

export function divisorDoIcone(url: string, m: Material): Divisor {
  const nome = url.slice(url.lastIndexOf('/') + 1);
  const tabela = DIVISOR_POR_ARQUIVO[nome];
  if (tabela !== undefined) return m.id === 'shadow-iron' ? tabela / FATOR_BRILHO_SHADOW : tabela;
  if (url.includes('/plates/') || url.includes('/armor/')) return 160;
  return 'media';
}
